import React, { useState, useEffect, useContext } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import EmailIcon from '@mui/icons-material/Email';
import DraftsIcon from '@mui/icons-material/Drafts';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import DataTable from '../../../sharedModules/dataTable/DataTable/DataTable';
import { IDataTableColumn } from '../../../sharedModules/dataTable/DataTable/model';
import axios from '../../../sharedModules/utils/axios.utils';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

const columns: IDataTableColumn<any>[] = [
  {
    name: 'Subject',
    selector: 'subject',
    sortable: true,
    wrap: true,
    width: '300px',
  },
  {
    name: 'From',
    selector: 'from_name',
    sortable: true,
    wrap: true,
    format: (row: any) => {
      return (
        <div>
          <div style={{ fontWeight: 500 }}>{row.from_name || '-'}</div>
          <div style={{ fontSize: '0.85em', color: '#666' }}>{row.from_address || '-'}</div>
        </div>
      );
    },
  },
  {
    name: 'Account',
    selector: 'email_account',
    sortable: true,
    wrap: true,
  },
  {
    name: 'Vendor',
    selector: 'vendor_name',
    sortable: true,
    wrap: true,
    format: (row: any) => row.vendor_name || '-',
  },
  {
    name: 'Received',
    selector: 'received_at',
    sortable: true,
    format: (row: any) => {
      if (!row.received_at) return '-';
      const date = new Date(row.received_at);
      return (
        <div>
          <div>{date.toLocaleDateString()}</div>
          <div style={{ fontSize: '0.85em', color: '#666' }}>{date.toLocaleTimeString()}</div>
        </div>
      );
    },
  },
  {
    name: 'Status',
    selector: 'is_read',
    sortable: true,
    format: (row: any) => {
      if (row.is_read) {
        return <Chip label="Read" size="small" icon={<DraftsIcon fontSize="small" />} />;
      }
      return <Chip label="Unread" color="primary" size="small" icon={<EmailIcon fontSize="small" />} />;
    },
  },
  {
    name: 'Attachments',
    selector: 'has_attachments',
    sortable: true,
    format: (row: any) => {
      if (row.has_attachments) {
        return <AttachFileIcon fontSize="small" color="action" />;
      }
      return '-';
    },
  },
  {
    name: 'Issue',
    selector: 'processing_note',
    sortable: true,
    format: (row: any) =>
      row.processing_note ? (
        <Tooltip title={row.processing_note}>
          <WarningAmberIcon fontSize="small" style={{ color: '#ed6c02' }} />
        </Tooltip>
      ) : (
        '-'
      ),
  },
];

const OutlookEmailListPage: React.FunctionComponent = () => {
  const history = useHistory();
  const location = useLocation();
  const { showErrorSnackBar } = useContext(SnackBarContext);

  const queryParams = new URLSearchParams(location.search);
  const initialSearch = queryParams.get('search') || '';
  const initialAccount = queryParams.get('account') || 'all';
  const initialFolder = queryParams.get('folder') || 'all';
  const initialReadStatus = queryParams.get('read_status') || 'all';
  const initialAttachments = queryParams.get('attachments') || 'true';

  const [emails, setEmails] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>(initialSearch);
  const [accountFilter, setAccountFilter] = useState<string>(initialAccount);
  const [folderFilter, setFolderFilter] = useState<string>(initialFolder);
  const [readStatusFilter, setReadStatusFilter] = useState<string>(initialReadStatus);
  const [attachmentFilter, setAttachmentFilter] = useState<string>(initialAttachments);
  const [loading, setLoading] = useState<boolean>(false);

  const [accounts, setAccounts] = useState<string[]>([]);
  const [folders, setFolders] = useState<string[]>([]);

  const updateUrl = (search: string, account: string, folder: string, readStatus: string, attachments: string) => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (account !== 'all') params.set('account', account);
    if (folder !== 'all') params.set('folder', folder);
    if (readStatus !== 'all') params.set('read_status', readStatus);
    if (attachments !== 'true') params.set('attachments', attachments);
    const qs = params.toString();
    history.replace(`/vendors/outlook-emails${qs ? `?${qs}` : ''}`);
  };

  const fetchEmails = async (search = '', account = 'all', folder = 'all', readStatus = 'all', attachments = 'all') => {
    setLoading(true);
    try {
      const params: any = {};
      if (search) params.search = search;
      if (account !== 'all') params.email_account = account;
      if (folder !== 'all') params.folder = folder;
      if (readStatus !== 'all') params.is_read = readStatus;
      if (attachments !== 'all') params.has_attachments = attachments;

      const response: any = await axios.get('/vendors/outlook_emails', { params });
      const emailData = response.data || [];
      setEmails(emailData);

      const uniqueAccounts = [...new Set(emailData.map((e: any) => e.email_account))].filter(Boolean);
      const uniqueFolders = [...new Set(emailData.map((e: any) => e.folder))].filter(Boolean);
      setAccounts(uniqueAccounts as string[]);
      setFolders(uniqueFolders as string[]);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to fetch Outlook emails';
      showErrorSnackBar(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    updateUrl(searchTerm, accountFilter, folderFilter, readStatusFilter, attachmentFilter);
    fetchEmails(searchTerm, accountFilter, folderFilter, readStatusFilter, attachmentFilter);
  };

  const handleAccountChange = (newAccount: string) => {
    setAccountFilter(newAccount);
    updateUrl(searchTerm, newAccount, folderFilter, readStatusFilter, attachmentFilter);
    fetchEmails(searchTerm, newAccount, folderFilter, readStatusFilter, attachmentFilter);
  };

  const handleFolderChange = (newFolder: string) => {
    setFolderFilter(newFolder);
    updateUrl(searchTerm, accountFilter, newFolder, readStatusFilter, attachmentFilter);
    fetchEmails(searchTerm, accountFilter, newFolder, readStatusFilter, attachmentFilter);
  };

  const handleReadStatusChange = (newStatus: string) => {
    setReadStatusFilter(newStatus);
    updateUrl(searchTerm, accountFilter, folderFilter, newStatus, attachmentFilter);
    fetchEmails(searchTerm, accountFilter, folderFilter, newStatus, attachmentFilter);
  };

  const handleAttachmentChange = (newFilter: string) => {
    setAttachmentFilter(newFilter);
    updateUrl(searchTerm, accountFilter, folderFilter, readStatusFilter, newFilter);
    fetchEmails(searchTerm, accountFilter, folderFilter, readStatusFilter, newFilter);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  useEffect(() => {
    fetchEmails(initialSearch, initialAccount, initialFolder, initialReadStatus, initialAttachments);
  }, []);

  return (
    <Box style={{ width: '100%' }}>
      <Box>
        <Grid justifyContent="space-between" container spacing={3}>
          <Grid item>
            <Typography variant="h5" component="h5" style={{ marginTop: 5 }}>
              Outlook Emails
            </Typography>
          </Grid>
        </Grid>
        <br />
        <Grid container spacing={2} alignItems="flex-start" onKeyDown={handleKeyPress} style={{ marginBottom: 16 }}>
          <Grid item xs={12} md={3}>
            <TextField
              label="Search by Subject, Name, or Email"
              variant="standard"
              style={{ width: '100%', marginTop: 8 }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Enter search term"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl variant="standard" style={{ width: '100%', marginTop: 8 }}>
              <InputLabel>Email Account</InputLabel>
              <Select
                value={accountFilter}
                onChange={(e) => handleAccountChange(e.target.value as string)}
                label="Email Account"
              >
                <MenuItem value="all">All Accounts</MenuItem>
                {accounts.map((account) => (
                  <MenuItem key={account} value={account}>
                    {account}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl variant="standard" style={{ width: '100%', marginTop: 8 }}>
              <InputLabel>Read Status</InputLabel>
              <Select
                value={readStatusFilter}
                onChange={(e) => handleReadStatusChange(e.target.value as string)}
                label="Read Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">Read</MenuItem>
                <MenuItem value="false">Unread</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl variant="standard" style={{ width: '100%', marginTop: 8 }}>
              <InputLabel>Attachments</InputLabel>
              <Select
                value={attachmentFilter}
                onChange={(e) => handleAttachmentChange(e.target.value as string)}
                label="Attachments"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">With Attachments</MenuItem>
                <MenuItem value="false">Without Attachments</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={1}>
            <Button onClick={handleSearch} variant="contained" style={{ marginTop: 20 }}>
              Search
            </Button>
          </Grid>
        </Grid>
        <Card>
          <CardContent>
            <DataTable
              title="Emails"
              columns={columns}
              data={emails}
              onRowClicked={(row) => history.push(`/vendors/outlook-emails/${row.id}`)}
              striped
              dense
              highlightOnHover
              pointerOnHover
              noHeader
              pagination
              progressPending={loading}
            />
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default OutlookEmailListPage;
